import { ZOHO_ASAP_APP_URL } from './servers';

const ASAP_LOADER_ID = 'zohodeskasapscript';
const DEFAULT_ASAP_SRC = 'https://desk.zoho.com/portal/api/web/asapApp/1285664000000489001?orgId=916298480';
const ASAP_SRC = ZOHO_ASAP_APP_URL || DEFAULT_ASAP_SRC;

const UI_SELECTORS = [
  '[id*="zohodesk" i]',
  '[class*="zohodesk" i]',
  '[id*="asap" i]',
  '[class*="asap" i]',
  'iframe[src*="desk.zoho.com" i]',
];

const SCRIPT_SELECTORS = [
  `script#${ASAP_LOADER_ID}`,
  'script[src*="desk.zoho.com/portal/api/web/asapApp" i]',
  'script[id*="zohodesk" i]',
  'script[id*="asap" i]',
];

const canUseDom = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const ensureZohoReadyQueue = () => {
  if (!canUseDom()) return;
  if (window.ZohoDeskAsapReady) return;

  // Mirrors Zoho's snippet behavior: queue callbacks until the script flips ReadyStatus.
  window.ZohoDeskAsapReady = function ZohoDeskAsapReady(cb) {
    if (!window.ZohoDeskAsap__asyncalls) window.ZohoDeskAsap__asyncalls = [];
    const calls = window.ZohoDeskAsap__asyncalls;

    if (!window.ZohoDeskAsapReadyStatus) {
      if (cb) calls.push(cb);
      return;
    }

    if (cb) calls.push(cb);
    calls.forEach((fn) => fn && fn());
    window.ZohoDeskAsap__asyncalls = null;
  };
};

class ZohoAsapService {
  constructor() {
    this.enabled = false;
    this.observer = null;
    this.hasEverInjected = false;
  }

  enable() {
    this.enabled = true;

    if (!canUseDom()) return;

    // stop blocking removals
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // already injected
    if (document.getElementById(ASAP_LOADER_ID) || document.querySelector('script[src*="asapApp" i]')) {
      this.hasEverInjected = true;
      return;
    }

    ensureZohoReadyQueue();

    // inject loader script
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.id = ASAP_LOADER_ID;
    s.defer = true;
    s.src = ASAP_SRC;
    s.crossOrigin = 'anonymous';

    const t = document.getElementsByTagName('script')[0];
    if (t && t.parentNode) t.parentNode.insertBefore(s, t);
    else (document.head || document.body || document.documentElement).appendChild(s);

    this.hasEverInjected = true;
  }

  disable() {
    this.enabled = false;

    if (!canUseDom()) return;

    const removeAsapDom = () => {
      UI_SELECTORS.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => el.remove());
      });

      SCRIPT_SELECTORS.forEach((selector) => {
        document.querySelectorAll(selector).forEach((el) => el.remove());
      });
    };

    // clean now
    removeAsapDom();

    // Keep it gone (SPA safe) only if it's relevant (reduces global observer overhead).
    const shouldBlockReinjection = (
      this.hasEverInjected
      || UI_SELECTORS.some((s) => document.querySelector(s))
      || SCRIPT_SELECTORS.some((s) => document.querySelector(s))
    );

    if (shouldBlockReinjection && !this.observer && typeof MutationObserver !== 'undefined') {
      this.observer = new MutationObserver(() => {
        if (!this.enabled) removeAsapDom();
      });
      this.observer.observe(document.documentElement, { childList: true, subtree: true });
    }

    // clean globals
    try {
      delete window.ZohoDeskAsapReady;
      delete window.ZohoDeskAsapReadyStatus;
      delete window.ZohoDeskAsap__asyncalls;
    } catch (e) {
      // ignore
    }
  }
}

export const zohoAsapService = new ZohoAsapService();
